/**
 * Supplier profile: the terms a supplier declares about itself (table `provider_profiles`
 * in the monorepo, migration 20261009000014) and merchants read before connecting.
 *
 * Everything here is pure so the supplier form, the merchant card and the comparison table
 * agree on one reading of the data, and so it can be tested without a database.
 */

export interface ShippingWindow {
  min_days: number;
  max_days: number;
}

/**
 * Keyed by region. A region that is ABSENT means the supplier does not ship there; that is
 * the contract, so a blank row in the form is dropped rather than stored as nulls.
 */
export type ShippingTimes = Record<string, ShippingWindow>;

export type ShippingCostUnit = 'per_label' | 'per_order' | 'per_item' | 'included';
export type VatTreatment = 'margin' | 'vat_included' | 'vat_excluded' | 'mixed';

export interface SupplierProfile {
  provider_id: string;
  ships_from_country: string | null;
  shipping_times: ShippingTimes;
  shipping_cost_amount: number | null;
  shipping_cost_currency: string;
  shipping_cost_unit: ShippingCostUnit | null;
  vat_treatment: VatTreatment | null;
  max_catalog_size: number | null;
  notes: string | null;
  /** Open bag for fields collected from supplier calls before they earn a column. */
  extra: Record<string, unknown>;
  /** FlowStock-managed: how often we refresh this supplier's stock. Not supplier-editable. */
  sync_interval_minutes: number | null;
  updated_at?: string | null;
}

/** Columns a supplier may write. Mirrors the column grants in the migration. */
export type SupplierProfileInput = Pick<
  SupplierProfile,
  | 'ships_from_country'
  | 'shipping_times'
  | 'shipping_cost_amount'
  | 'shipping_cost_currency'
  | 'shipping_cost_unit'
  | 'vat_treatment'
  | 'max_catalog_size'
  | 'notes'
>;

export const PROFILE_COLUMNS =
  'provider_id, ships_from_country, shipping_times, shipping_cost_amount, shipping_cost_currency, ' +
  'shipping_cost_unit, vat_treatment, max_catalog_size, notes, extra, sync_interval_minutes, updated_at';

/** Regions offered in the form, in display order. The column itself accepts any key. */
export const SHIPPING_REGIONS: { key: string; label: string }[] = [
  { key: 'NL', label: 'Netherlands' },
  { key: 'EU', label: 'EU' },
  { key: 'WORLD', label: 'Worldwide' },
];

export const SHIPPING_COST_UNITS: { value: ShippingCostUnit; label: string }[] = [
  { value: 'per_label', label: 'per label' },
  { value: 'per_order', label: 'per order' },
  { value: 'per_item', label: 'per item' },
  { value: 'included', label: 'included in the price' },
];

export const VAT_TREATMENTS: { value: VatTreatment; label: string; hint: string }[] = [
  { value: 'margin', label: 'Margin scheme', hint: 'No VAT on the invoice; the buyer cannot reclaim VAT.' },
  { value: 'vat_included', label: 'VAT included', hint: 'Prices include VAT, shown on the invoice.' },
  { value: 'vat_excluded', label: 'VAT excluded', hint: 'Prices are net; VAT is added on the invoice.' },
  { value: 'mixed', label: 'Mixed', hint: 'Depends on the item; stated per product.' },
];

/** Countries suppliers ship from today, plus the rest of the EU and the usual neighbours. */
export const COUNTRIES: { code: string; name: string }[] = [
  ['NL', 'Netherlands'], ['BE', 'Belgium'], ['DE', 'Germany'], ['PL', 'Poland'], ['FR', 'France'],
  ['IT', 'Italy'], ['ES', 'Spain'], ['PT', 'Portugal'], ['AT', 'Austria'], ['LU', 'Luxembourg'],
  ['IE', 'Ireland'], ['DK', 'Denmark'], ['SE', 'Sweden'], ['FI', 'Finland'], ['EE', 'Estonia'],
  ['LV', 'Latvia'], ['LT', 'Lithuania'], ['CZ', 'Czechia'], ['SK', 'Slovakia'], ['HU', 'Hungary'],
  ['SI', 'Slovenia'], ['HR', 'Croatia'], ['RO', 'Romania'], ['BG', 'Bulgaria'], ['GR', 'Greece'],
  ['CY', 'Cyprus'], ['MT', 'Malta'], ['GB', 'United Kingdom'], ['CH', 'Switzerland'], ['NO', 'Norway'],
  ['US', 'United States'], ['TR', 'Türkiye'], ['CN', 'China'], ['HK', 'Hong Kong'],
].map(([code, name]) => ({ code, name }));

export function countryName(code: string | null | undefined): string | null {
  if (!code) return null;
  return COUNTRIES.find((c) => c.code === code)?.name ?? code;
}

export function regionLabel(key: string): string {
  return SHIPPING_REGIONS.find((r) => r.key === key)?.label ?? key;
}

/** A stored window is only trusted if it is two sane whole numbers in order. */
function validWindow(w: unknown): w is ShippingWindow {
  if (!w || typeof w !== 'object') return false;
  const { min_days, max_days } = w as Record<string, unknown>;
  return (
    Number.isInteger(min_days) && Number.isInteger(max_days) &&
    (min_days as number) >= 0 && (max_days as number) >= (min_days as number)
  );
}

/** Shipping windows worth showing: known regions first in form order, then any others. */
export function shippingRows(times: ShippingTimes | null | undefined): { key: string; label: string; window: ShippingWindow }[] {
  const t = times && typeof times === 'object' ? times : {};
  const known = SHIPPING_REGIONS.map((r) => r.key);
  const keys = [...known.filter((k) => k in t), ...Object.keys(t).filter((k) => !known.includes(k)).sort()];
  return keys.filter((k) => validWindow(t[k])).map((k) => ({ key: k, label: regionLabel(k), window: t[k] }));
}

export function formatShippingWindow(w: ShippingWindow | null | undefined): string {
  if (!validWindow(w)) return 'Does not ship';
  if (w.min_days === w.max_days) return `${w.min_days} day${w.min_days === 1 ? '' : 's'}`;
  return `${w.min_days}–${w.max_days} days`;
}

export function formatShippingCost(p: Pick<SupplierProfile, 'shipping_cost_amount' | 'shipping_cost_currency' | 'shipping_cost_unit'> | null | undefined): string | null {
  if (!p) return null;
  if (p.shipping_cost_unit === 'included') return 'Included in the price';
  if (p.shipping_cost_amount === null || p.shipping_cost_amount === undefined) return null;
  const amount = Number(p.shipping_cost_amount);
  if (!Number.isFinite(amount)) return null;
  const money = `${Number.isInteger(amount) ? amount : amount.toFixed(2)} ${p.shipping_cost_currency || 'EUR'}`;
  const unit = SHIPPING_COST_UNITS.find((u) => u.value === p.shipping_cost_unit)?.label;
  return unit ? `${money} ${unit}` : money;
}

export function formatVat(v: VatTreatment | null | undefined): string | null {
  return VAT_TREATMENTS.find((t) => t.value === v)?.label ?? null;
}

export function formatSyncInterval(minutes: number | null | undefined): string | null {
  if (!minutes || minutes <= 0) return null;
  if (minutes < 60) return `Every ${minutes} min`;
  const hours = minutes / 60;
  return Number.isInteger(hours) ? `Every ${hours} h` : `Every ${minutes} min`;
}

export function formatCatalogSize(n: number | null | undefined): string | null {
  if (!n || n <= 0) return null;
  return `Up to ${n.toLocaleString('en-US')} products`;
}

/**
 * Has the supplier told merchants anything? The sync interval is ours, so a row holding
 * only that (every seeded row starts this way) still counts as an unfilled profile.
 */
export function hasSupplierTerms(p: SupplierProfile | null | undefined): boolean {
  if (!p) return false;
  return Boolean(
    p.ships_from_country ||
    shippingRows(p.shipping_times).length > 0 ||
    formatShippingCost(p) ||
    p.vat_treatment ||
    p.max_catalog_size ||
    (p.notes && p.notes.trim()),
  );
}

/** Form state: everything is a string while the supplier is typing. */
export interface SupplierProfileFormState {
  ships_from_country: string;
  shipping: Record<string, { min: string; max: string }>;
  shipping_cost_amount: string;
  shipping_cost_currency: string;
  shipping_cost_unit: ShippingCostUnit | '';
  vat_treatment: VatTreatment | '';
  max_catalog_size: string;
  notes: string;
}

export function toFormState(p: SupplierProfile | null | undefined): SupplierProfileFormState {
  const shipping: SupplierProfileFormState['shipping'] = {};
  SHIPPING_REGIONS.forEach(({ key }) => {
    const w = p?.shipping_times?.[key];
    shipping[key] = validWindow(w) ? { min: String(w.min_days), max: String(w.max_days) } : { min: '', max: '' };
  });
  return {
    ships_from_country: p?.ships_from_country ?? '',
    shipping,
    shipping_cost_amount:
      p?.shipping_cost_amount === null || p?.shipping_cost_amount === undefined ? '' : String(p.shipping_cost_amount),
    shipping_cost_currency: p?.shipping_cost_currency || 'EUR',
    shipping_cost_unit: p?.shipping_cost_unit ?? '',
    vat_treatment: p?.vat_treatment ?? '',
    max_catalog_size: p?.max_catalog_size ? String(p.max_catalog_size) : '',
    notes: p?.notes ?? '',
  };
}

const wholeNumber = (s: string) => /^\d+$/.test(s.trim());

/**
 * Turn form state into the row to save, or the reasons it cannot be saved.
 *
 * `existingTimes` carries regions the form does not offer (added by hand or by a later
 * version) through a save untouched, so editing NL/EU/Worldwide never deletes another region.
 */
export function buildProfileInput(
  form: SupplierProfileFormState,
  existingTimes: ShippingTimes | null | undefined = {},
): { input: SupplierProfileInput | null; errors: string[] } {
  const errors: string[] = [];
  const offered = SHIPPING_REGIONS.map((r) => r.key);

  const shipping_times: ShippingTimes = {};
  Object.entries(existingTimes ?? {}).forEach(([k, w]) => {
    if (!offered.includes(k) && validWindow(w)) shipping_times[k] = w;
  });

  SHIPPING_REGIONS.forEach(({ key, label }) => {
    const min = form.shipping[key]?.min.trim() ?? '';
    const max = form.shipping[key]?.max.trim() ?? '';
    if (!min && !max) return; // blank = does not ship there
    if (!wholeNumber(min || max) || !wholeNumber(max || min)) {
      errors.push(`${label}: shipping time must be whole days.`);
      return;
    }
    // One number filled in means a fixed time, not a mistake.
    const lo = Number(min || max);
    const hi = Number(max || min);
    if (hi < lo) {
      errors.push(`${label}: the longest shipping time cannot be shorter than the fastest.`);
      return;
    }
    if (hi > 90) {
      errors.push(`${label}: shipping time looks too long (over 90 days).`);
      return;
    }
    shipping_times[key] = { min_days: lo, max_days: hi };
  });

  const included = form.shipping_cost_unit === 'included';
  let shipping_cost_amount: number | null = null;
  const rawAmount = form.shipping_cost_amount.trim().replace(',', '.');
  if (!included && rawAmount) {
    const n = Number(rawAmount);
    if (!Number.isFinite(n) || n < 0) errors.push('Shipping cost must be a number, 0 or more.');
    else shipping_cost_amount = Math.round(n * 100) / 100;
  }
  if (shipping_cost_amount !== null && !form.shipping_cost_unit) {
    errors.push('Say what the shipping cost is charged per (label, order or item).');
  }

  let max_catalog_size: number | null = null;
  const rawMax = form.max_catalog_size.trim();
  if (rawMax) {
    if (!wholeNumber(rawMax) || Number(rawMax) <= 0) errors.push('Catalogue size must be a whole number above 0.');
    else max_catalog_size = Number(rawMax);
  }

  const notes = form.notes.trim();
  if (notes.length > 1000) errors.push('Notes can be at most 1,000 characters.');

  if (errors.length) return { input: null, errors };
  return {
    errors,
    input: {
      ships_from_country: form.ships_from_country || null,
      shipping_times,
      shipping_cost_amount,
      shipping_cost_currency: (form.shipping_cost_currency || 'EUR').toUpperCase(),
      shipping_cost_unit: form.shipping_cost_unit || null,
      vat_treatment: form.vat_treatment || null,
      max_catalog_size,
      notes: notes || null,
    },
  };
}
